import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatNaira, toKobo, type Kobo } from '@unifemga/shared';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Money arrives from the API as an integer number of **kobo**, serialised to a
 * string — never a JSON number, because a JSON number is a double and would
 * lose precision on large naira figures.
 *
 * So this parses, it does not convert: `"70000000"` is ₦700,000, not ₦70,000,000.
 * Multiplying here was a real bug that rendered every figure 100× too large.
 */
export function parseMoney(value: string | number | null | undefined): Kobo {
  if (value === null || value === undefined || value === '') return 0n;

  const text = String(value).trim();

  // A decimal point means someone sent naira rather than kobo. Convert it
  // rather than silently truncating, but this should not happen — the API
  // emits kobo integers throughout.
  if (text.includes('.')) return toKobo(text);

  if (!/^-?\d+$/.test(text)) return 0n;
  return BigInt(text);
}

/** Converts a naira amount the user typed into kobo, for sending to the API. */
/**
 * Group an amount as it is typed: `20100` reads as `20,100`.
 *
 * Six figures without separators is a number people miscount, and the whole
 * risk is entering ten times what you meant. Kept as a string so a trailing
 * decimal point survives while it is being typed.
 */
export function groupNairaInput(input: string): string {
  const cleaned = input.replace(/[^\d.]/g, '');
  const [whole = '', ...rest] = cleaned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (rest.length === 0) return grouped;
  return `${grouped}.${rest.join('').slice(0, 2)}`;
}

export function nairaInputToKobo(input: string): Kobo {
  const trimmed = input.replace(/,/g, '').trim();
  if (!trimmed) return 0n;
  try {
    return toKobo(trimmed);
  } catch {
    return 0n;
  }
}

/** `"500000.00"` → `"₦500,000.00"`. */
export function money(value: string | number | null | undefined, compact = false): string {
  return formatNaira(parseMoney(value), { compact });
}

/** `"500000.00"` → `"₦500,000"`, for headline tiles where kobo is noise. */
export function moneyRounded(value: string | number | null | undefined): string {
  return formatNaira(parseMoney(value), { showDecimals: false });
}

/** "12 September 2026 at 5:12 pm" — a timestamp, not just the day. */
export function formatDateTime(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })} at ${date.toLocaleTimeString('en-NG', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}`;
}

const PAYMENT_METHODS: Record<string, string> = {
  BANK_TRANSFER: 'Bank transfer',
  CASH: 'Cash',
  CHEQUE: 'Cheque',
  POS: 'POS',
  DIRECT_DEBIT: 'Direct debit',
  OTHER: 'Other',
};

/** "BANK_TRANSFER" reads as "Bank transfer", not "bank transfer". */
export function paymentMethodLabel(value: string) {
  return PAYMENT_METHODS[value] ?? value.replace(/_/g, ' ').toLowerCase();
}
